// src/components/layout/Layout.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  AlertTriangle, 
  Calendar, 
  FileText, 
  Bell, 
  User, 
  Search,
  X, 
  ArrowLeft,
  Command,
  LogOut,
  Settings,
  HelpCircle
} from 'lucide-react';
import { verifySession, logoutUser } from '../../utils/auth';

const Layout = ({ children }) => {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const PORTAL_URL = process.env.REACT_APP_PORTAL_URL || 'https://aplicacionesfcn.com';

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.trim().split(' ');
    if (names.length >= 2) return (names[0][0] + names[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  // Detectar Ctrl/Cmd + K para Command Palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
      if (e.key === 'Escape') {
        setCommandPaletteOpen(false);
        setSearchQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const sessionData = await verifySession();
        if (sessionData.success && sessionData.user) {
          setUserData(sessionData.user);
          console.log('Usuario cargado en Layout:', sessionData.user);
        }
      } catch (error) {
        console.error('Error obteniendo usuario:', error);
      }
    };

    fetchUser();
  }, []);

  const menuItems = [
    { 
      id: 'dashboard', 
      icon: LayoutDashboard, 
      label: 'Resumen', 
      path: '/',
      description: 'Vista general y estadísticas del sistema'
    },
    { 
      id: 'emergencias', 
      icon: AlertTriangle, 
      label: 'Emergencias', 
      path: '/emergencias',
      description: 'Gestión y registro de emergencias viales'
    },
    { 
      id: 'eventos', 
      icon: Calendar, 
      label: 'Eventos', 
      path: '/eventos',
      description: 'Registro de eventos'
    },
    { 
      id: 'reportes', 
      icon: FileText, 
      label: 'Reportes', 
      path: '/reportes',
      description: 'Informes, análisis y estadísticas'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setCommandPaletteOpen(false);
    setSearchQuery('');
  };

  // Solo regresar al portal SIN cerrar sesión
  const handleRegresarPortal = () => {
    window.location.href = PORTAL_URL;
  };

  // Cerrar sesión completamente
  const handleLogout = () => {
    if (window.confirm('¿Estás seguro que deseas cerrar sesión?')) {
      logoutUser();
    }
  };

  const isActive = (path) => location.pathname === path;

  // Filtrar items para command palette
  const filteredItems = menuItems.filter(item =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100">
      {/* HEADER TOP - Fixed */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="h-16 px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4 max-w-[2000px] mx-auto">
          
          {/* LEFT: Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#c8a88c] to-[#b8987c] rounded-xl shadow-lg p-2">
              <img src="/logo192.png" alt="FCN" className="w-full h-full object-contain" />
            </div>
            <div className="hidden sm:block">
              <div className="font-bold text-gray-900 text-base leading-tight">FCN-EMERGENCIAS</div>
              <div className="text-xs text-gray-500 leading-tight">Control de Emergencias en la red vial cafetalera</div>
            </div>
          </div>

          {/* CENTER: Command Palette Trigger */}
          <button
            onClick={() => setCommandPaletteOpen(true)}
            className="flex items-center gap-3 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm text-gray-600 transition-all max-w-md flex-1 group"
          >
            <Search className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            <span className="flex-1 text-left hidden sm:inline">Buscar secciones...</span>
            
          </button>

          {/* RIGHT: Actions + User */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleRegresarPortal}
              title="Regresar al portal"
              className="p-2 hover:bg-gray-100 rounded-xl text-gray-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            

            {userData && (
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-200">
                <div className="w-9 h-9 bg-gradient-to-br from-[#c8a88c] to-[#b8987c] rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-md">
                  {getInitials(userData.NombreCompleto || userData.empleado?.NombreEmpleado)}
                </div>
                <div className="hidden lg:block max-w-[140px]">
                  <div className="text-sm font-semibold text-gray-900 truncate leading-tight">
                    {(userData.NombreCompleto || userData.empleado?.NombreEmpleado || '').split(' ')[0]}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    Usuario
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* MAIN CONTENT - Full Width */}
      <main className="w-full min-h-[calc(100vh-4rem)]">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>

      {/* COMMAND PALETTE - Spotlight Style */}
      {commandPaletteOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 animate-fadeIn"
            onClick={() => {
              setCommandPaletteOpen(false);
              setSearchQuery('');
            }}
          />
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] px-4">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-slideDown">
              {/* Search Input */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
                <Search className="w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar secciones..."
                  autoFocus
                  className="flex-1 outline-none text-gray-900 placeholder-gray-400 text-base"
                />
                <button
                  onClick={() => {
                    setCommandPaletteOpen(false);
                    setSearchQuery('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Cerrar"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Results */}
              <div className="max-h-[420px] overflow-y-auto p-2">
                {filteredItems.length > 0 ? (
                  <div className="space-y-1">
                    {filteredItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleNavigation(item.path)}
                          className={`
                            w-full flex items-center gap-4 px-4 py-3.5 rounded-xl
                            transition-all duration-200 text-left group
                            ${active 
                              ? 'bg-gradient-to-r from-[#c8a88c] to-[#b8987c] text-white shadow-md' 
                              : 'hover:bg-gray-50'
                            }
                          `}
                        >
                          <div className={`
                            p-2.5 rounded-lg 
                            ${active ? 'bg-white/20' : 'bg-gray-100 group-hover:bg-gray-200'}
                          `}>
                            <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`font-semibold text-base mb-0.5 ${active ? 'text-white' : 'text-gray-900'}`}>
                              {item.label}
                            </div>
                            <div className={`text-sm ${active ? 'text-white/80' : 'text-gray-500'}`}>
                              {item.description}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-12 text-center text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium text-base">No se encontraron resultados</p>
                    <p className="text-sm mt-1">Intenta con otro término de búsqueda</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-100 px-5 py-3 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
                
                <div className="sm:hidden text-gray-600">
                  Toca una opción para navegar
                </div>
                <span className="hidden sm:inline">ESC para cerrar</span>
              </div>

              
            </div>
          </div>
        </>
      )}

      {/* Animations */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-slideDown {
          animation: slideDown 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .animate-fadeIn {
          animation: fadeIn 0.15s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Layout;